import type {
  CuratedReferenceDto,
  RadarRunReceiptDto,
  RadarSetupDto,
  RadarUnavailableReason,
  ViralMediaType,
  ViralMetricsDto,
  ViralPostDto,
  ViralRadarDto,
  ViralScoreBreakdownDto,
} from '@devmil/contracts';
import {useState} from 'react';

import {
  useCreateTrendTopic,
  useViralRadar,
} from '../features/radar/useViralRadar.js';
import {formatDateTime, UNAVAILABLE} from '../lib/format.js';
import {describeProblem} from '../lib/panel-problem.js';

/**
 * Radar de virais.
 *
 * Tela nova, fora da prévia aprovada, herdando tokens e classes de
 * `app/styles.css`. O bloco de título vem da moldura de `PANEL_ROUTES`, como
 * nas outras páginas com entrada de menu, então este corpo começa direto no
 * painel.
 *
 * ## O que esta tela NÃO tem, e é a parte que importa
 *
 * Não existe aqui botão de repostar, de baixar mídia, de curtir, de seguir, de
 * comentar nem de salvar. Não é esquecimento e não é etapa futura: o pedido
 * original era repostar mídia de terceiro, ele foi recusado por direito
 * autoral, pelos termos da plataforma e pelo risco à conta, e o dono aprovou
 * este radar no lugar. A ausência desses botões é a decisão, desenhada.
 *
 * O único gesto de publicação continua sendo do dono, no aplicativo do
 * Instagram, com o compartilhamento nativo, que credita o autor sozinho. A
 * tela diz isso por escrito e não tenta imitar o gesto.
 */

/** Contagem inteira, no formato do painel. */
const CONTAGEM = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 0});

/** Nota e componentes, sempre com duas casas: `0,80` e `0,8` seriam lidos como
 * precisões diferentes numa lista onde o olho compara linha com linha. */
const NOTA = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const FORMATO_LABEL: Record<ViralMediaType, string> = {
  REELS: 'Reel',
  VIDEO: 'Vídeo',
  IMAGE: 'Foto',
  CAROUSEL_ALBUM: 'Carrossel',
};

/**
 * Os quatro componentes da nota, na ordem em que o plano os descreve.
 *
 * Eles aparecem SEMPRE, e abertos: uma nota fechada é um número que o dono
 * teria de aceitar por confiança, e a pontuação existe justamente para ser
 * explicável campo a campo.
 */
const COMPONENTES: readonly (readonly [
  keyof ViralScoreBreakdownDto,
  string,
])[] = [
  ['engajamento', 'engajamento'],
  ['recencia', 'recência'],
  ['tema', 'tema'],
  ['formato', 'formato'],
];

/**
 * Cada motivo de indisponibilidade, em frase que o dono entende.
 *
 * O mapa é exaustivo por tipo: um motivo novo no contrato não compila sem
 * ganhar tradução aqui, que é o contrário de aparecer cru na tela.
 */
const MOTIVO_TEXTO: Record<RadarUnavailableReason, string> = {
  RADAR_SEM_CREDENCIAL:
    'Esta conta ainda não lê perfil de terceiro. Quem lê perfil alheio é a outra variante da API da plataforma, e ela exige uma Página do Facebook vinculada à conta profissional do Instagram. Essa vinculação é decisão sua, na Meta, e não um ajuste de código: enquanto ela não existir, o radar não tem de onde ler. O conector de leitura já está pronto e esperando esse segundo token, separado do conector que publica.',
  RADAR_SEM_PERFIS:
    'A lista curada de perfis ficou vazia depois dos filtros, então nenhuma consulta saiu nesta rodada. Não houve leitura, e por isso não há nada para ranquear.',
  RADAR_RECUSADO_PELA_GRAPH:
    'A plataforma respondeu, e respondeu recusando a leitura. O código que ela devolveu está logo abaixo: é ele que diz se falta permissão, se o perfil saiu do ar ou se a cota da janela acabou.',
  RADAR_TODOS_RECUSADOS:
    'A rodada saiu e perguntou a todos os perfis da lista. Todos recusaram, então nada chegou a ser lido. Isto não é "hoje não viralizou nada": nada foi consultado com sucesso, e o código da primeira recusa está logo abaixo.',
  RADAR_NAO_CONFIGURADO:
    'A lista curada de perfis não está configurada neste ambiente, então não existe a quem perguntar. Isto não é falta de acesso na plataforma: nenhuma credencial nova mudaria este estado, porque o que falta é a montagem deste lado.',
  RADAR_NUNCA_EXECUTADO:
    'A lista de perfis está montada e nenhuma rodada foi executada até agora. Não houve leitura, então não há o que ranquear e não há como afirmar nada sobre o mercado. Veja o quadro de montagem abaixo: sem horário configurado, ninguém roda sozinho.',
  RADAR_RODADA_INCOMPLETA:
    'A última rodada começou e não terminou, o que normalmente é o processo caindo no meio da leitura. Ela não produziu resultado, e mostrar a lista anterior como se fosse de hoje seria datar errado o que está na tela.',
  RADAR_RODADA_FALHOU:
    'A última rodada terminou em erro. O que ela deixou está no recibo abaixo, com a classe do erro; a mensagem da plataforma não é guardada, porque ela é texto livre de terceiro.',
};

const DESFECHO_TEXTO: Record<RadarRunReceiptDto['outcome'], string> = {
  em_andamento: 'em andamento',
  sucesso: 'concluída',
  parcial: 'concluída em parte',
  vazio: 'concluída sem tendência',
  indisponivel: 'sem leitura',
  falha: 'falha',
};

const DIREITOS_TEXTO: Record<CuratedReferenceDto['rights'], string> = {
  referencia_publica_creditada: 'referência pública, creditada',
  autorizado_pelo_autor: 'autorizado pelo autor',
  fonte_oficial: 'fonte oficial',
};

/** `2026-08-27` vira `27/08/2026`, sem passar perto de fuso. */
const diaLocal = (localDate: string): string =>
  localDate.split('-').reverse().join('/');

const metricasDaLinha = (metrics: ViralMetricsDto): string =>
  [
    `${CONTAGEM.format(metrics.likeCount)} curtidas`,
    `${CONTAGEM.format(metrics.commentCount)} comentários`,
    // Nem todo tipo de mídia devolve visualização. Zero seria uma medição, e
    // esta é a ausência de uma.
    metrics.viewCount === null
      ? `Visualizações ${UNAVAILABLE}`
      : `${CONTAGEM.format(metrics.viewCount)} visualizações`,
    `perfil com ${CONTAGEM.format(metrics.followerCount)} seguidores`,
  ].join(' · ');

const sinal = (valor: number): string =>
  `${valor > 0 ? '+' : ''}${CONTAGEM.format(valor)}`;

/**
 * A variação desde a primeira captura.
 *
 * Variação nula NUNCA vira zero na tela: "medi duas vezes e não mudou" é
 * notícia, "só medi uma vez" não é notícia nenhuma, e as duas sairiam iguais
 * se a ausência virasse `0`.
 */
const variacaoDaLinha = (metrics: ViralMetricsDto): string => {
  const partes = [
    metrics.deltaLikes === null
      ? null
      : `${sinal(metrics.deltaLikes)} curtidas`,
    metrics.deltaComments === null
      ? null
      : `${sinal(metrics.deltaComments)} comentários`,
  ].filter((parte): parte is string => parte !== null);

  return partes.length === 0
    ? 'Primeira leitura desta mídia: ainda não há variação para comparar'
    : `Desde a primeira leitura: ${partes.join(' e ')}`;
};

/**
 * A miniatura, que EXPIRA.
 *
 * O endereço é servido pela plataforma e tem prazo. Enquanto ele valer, a
 * imagem aparece; quando ele morrer, o `onError` troca por um quadro neutro
 * com o formato e o autor escritos, e a linha continua utilizável. Nada é
 * copiado para o nosso disco para "resolver" isso: baixar mídia de terceiro é
 * exatamente o que este plano recusa, e o quadro neutro é estado previsto, não
 * imagem quebrada.
 *
 * `referrerPolicy="no-referrer"` porque a requisição da imagem sai do
 * navegador do dono: sem isso, o endereço do painel viajaria no cabeçalho para
 * a rede de entrega da plataforma.
 */
const ViralThumb = ({post}: {post: ViralPostDto}) => {
  const [expirou, setExpirou] = useState(false);
  const formato = FORMATO_LABEL[post.mediaType];

  if (post.thumbnailUrl === null || expirou) {
    return (
      <span className="dm-piece-thumb dm-viral-thumb" data-testid="viral-thumb">
        <small>{formato}</small>
        <small>{`@${post.profileUsername}`}</small>
      </span>
    );
  }

  return (
    <span className="dm-piece-thumb dm-viral-thumb" data-testid="viral-thumb">
      <img
        src={post.thumbnailUrl}
        alt={`Miniatura do conteúdo de @${post.profileUsername}`}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => {
          setExpirou(true);
        }}
      />
    </span>
  );
};

const ViralRow = ({post}: {post: ViralPostDto}) => {
  const virarPauta = useCreateTrendTopic();

  return (
    // `data-profile` porque o nome do autor aparece DUAS vezes na linha quando
    // a miniatura cai no quadro neutro (no quadro e no título), e uma busca por
    // texto não teria como escolher a linha.
    <article
      className="dm-piece dm-viral"
      data-testid="viral-row"
      data-profile={post.profileUsername}
    >
      <ViralThumb post={post} />

      <div className="dm-viral-body">
        <div className="dm-viral-head">
          <span>
            <strong className="dm-piece-title">{`@${post.profileUsername}`}</strong>
            <small className="dm-piece-meta">
              {`${FORMATO_LABEL[post.mediaType]} · publicado em ${formatDateTime(post.postedAt)}`}
            </small>
          </span>
          <span className="dm-state">{`Nota ${NOTA.format(post.score)}`}</span>
        </div>

        <small className="dm-piece-meta">{metricasDaLinha(post.metrics)}</small>
        <small className="dm-piece-meta">{variacaoDaLinha(post.metrics)}</small>
        <small className="dm-piece-meta">
          {`Lido em ${formatDateTime(post.metrics.capturedAt)}`}
        </small>

        <ul className="dm-viral-score">
          {COMPONENTES.map(([chave, rotulo]) => (
            <li key={chave}>
              <span>{rotulo}</span>
              <strong>{NOTA.format(post.scoreBreakdown[chave])}</strong>
            </li>
          ))}
        </ul>

        <small className="dm-piece-meta">
          {post.themeTerms.length === 0
            ? 'Nenhum termo do tema casou nesta legenda'
            : `Termos do tema: ${post.themeTerms.join(', ')}`}
        </small>

        {/*
          A legenda alheia entra como TEXTO, e só. Ela é dado para o dono ler,
          nunca instrução: nada nesta tela decide coisa nenhuma a partir do que
          está escrito aqui dentro.
        */}
        {post.caption === null ? null : (
          <p className="dm-viral-caption">{post.caption}</p>
        )}

        {post.reading === null ? (
          <p className="dm-piece-meta">
            Sem leitura. A leitura por modelo está desligada, e leitura
            inventada seria pior que leitura nenhuma: a nota, os números e o
            endereço acima continuam valendo sem ela.
          </p>
        ) : (
          <div className="dm-viral-reading">
            <strong className="dm-piece-title">{post.reading.hook}</strong>
            <small className="dm-piece-meta">{post.reading.whyItWorked}</small>
            <small className="dm-piece-meta">
              {`Formato: ${post.reading.format}`}
            </small>
          </div>
        )}

        {virarPauta.error ? (
          <div className="dm-alert" role="alert">
            {describeProblem(virarPauta.error).message}
          </div>
        ) : null}

        <div className="dm-actions">
          {/*
            O original abre na plataforma, em aba nova. `noopener` e
            `noreferrer` não são enfeite: sem eles a página aberta recebe uma
            referência à nossa janela e o endereço do painel no cabeçalho.
          */}
          <a
            className="dm-action"
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir no Instagram
          </a>
          {post.topicId === null ? (
            <button
              className="dm-action dm-action-primary"
              type="button"
              disabled={virarPauta.isPending}
              onClick={() => {
                virarPauta.mutate(post.id);
              }}
            >
              Virar pauta
            </button>
          ) : (
            // Sem botão, e com o motivo escrito: a segunda pauta da mesma
            // tendência é o caminho para publicar duas peças sobre a mesma
            // coisa.
            <span className="dm-state is-approved">já virou pauta</span>
          )}
        </div>
      </div>
    </article>
  );
};

/**
 * O terceiro estado, e ele NÃO pode se parecer com lista vazia.
 *
 * Lista vazia afirma "hoje não viralizou nada", que seria mentira quando o
 * radar não teve de onde ler. Por isso este estado sai em caixa própria, com o
 * motivo em frase inteira e a primeira linha dizendo exatamente a diferença.
 */
const RadarIndisponivel = ({
  reason,
  graphCode,
}: {
  reason: RadarUnavailableReason;
  graphCode: string | null;
}) => (
  <div className="dm-alert" role="status">
    <p>
      <strong>
        O radar não teve de onde ler. Isto não é o mesmo que dizer que nada
        viralizou hoje.
      </strong>
    </p>
    <p>{MOTIVO_TEXTO[reason]}</p>
    <p className="dm-piece-meta">
      {`Motivo registrado: ${reason}`}
      {graphCode === null ? '' : ` · código da plataforma: ${graphCode}`}
    </p>
  </div>
);

/**
 * O RECIBO da última rodada, e por que ele fica sempre visível.
 *
 * Até 06/09/2026 esta página deduzia disponibilidade da configuração: caminho
 * da lista preenchido virava "o radar rodou", e a tela dizia que os perfis
 * tinham sido consultados num dia em que nenhuma rodada aconteceu (achado F14).
 *
 * O recibo é a fonte legítima daquela frase, e ele fica visível nos dois
 * estados porque a pergunta "quando foi a última leitura?" é a mesma quando a
 * lista está cheia e quando ela está vazia. Sem ele à vista, uma lista vazia
 * volta a ser ambígua.
 */
const ReciboDaRodada = ({recibo}: {recibo: RadarRunReceiptDto}) => (
  <div className="dm-viral-run" data-testid="radar-recibo">
    <small className="dm-piece-meta">
      {`Última rodada: ${formatDateTime(recibo.startedAt)} · ${DESFECHO_TEXTO[recibo.outcome]}`}
      {recibo.finishedAt === null
        ? ' · sem hora de término registrada'
        : ` · terminou em ${formatDateTime(recibo.finishedAt)}`}
      {recibo.trigger === 'agendador' ? ' · pelo agendador' : ' · à mão'}
    </small>
    <ul className="dm-viral-score">
      <li>
        <span>perfis pedidos</span>
        <strong>{CONTAGEM.format(recibo.profilesRequested)}</strong>
      </li>
      <li>
        <span>lidos</span>
        <strong>{CONTAGEM.format(recibo.profilesRead)}</strong>
      </li>
      <li>
        <span>recusados</span>
        <strong>{CONTAGEM.format(recibo.profilesRefused)}</strong>
      </li>
      <li>
        <span>mídias elegíveis</span>
        <strong>{CONTAGEM.format(recibo.mediaEligible)}</strong>
      </li>
    </ul>
    {recibo.graphCode === null ? null : (
      <small className="dm-piece-meta">
        {`Código registrado na rodada: ${recibo.graphCode}`}
      </small>
    )}
  </div>
);

/**
 * O que está MONTADO, que é uma pergunta diferente de o que foi LIDO.
 *
 * A auditoria mediu, em produção, a lista de perfis presente e o horário
 * ausente. As duas ausências levam a passos diferentes do dono, e a segunda
 * explica sozinha por que nada roda: sem horário, a tarefa nem entra na agenda.
 * Confundir isto com disponibilidade foi o defeito que este bloco separa.
 */
const MontagemDoRadar = ({setup}: {setup: RadarSetupDto}) => (
  <p className="dm-piece-meta" data-testid="radar-montagem">
    {`Montagem: lista de perfis ${setup.perfisConfigurados ? 'configurada' : UNAVAILABLE}`}
    {` · horários ${setup.horariosConfigurados ? 'configurados' : `${UNAVAILABLE} (ninguém roda sozinho)`}`}
    {` · curadoria ${setup.curadoriaConfigurada ? 'configurada' : UNAVAILABLE}`}
  </p>
);

/**
 * Uma referência curada por gente, e ela NUNCA se apresenta como descoberta.
 *
 * Ela não tem nota, não tem curtida e não tem miniatura, e isso é a verdade
 * dela: ninguém mediu o post que uma pessoa olhou. O que ela tem é crédito,
 * data, direito declarado e o motivo escrito por quem curou.
 *
 * O botão é o MESMO da tendência descoberta, e chama a mesma rota. É o que faz
 * a curadoria ser um caminho de verdade enquanto o radar externo não existe, em
 * vez de uma lista para o dono olhar.
 */
const CuratedRow = ({referencia}: {referencia: CuratedReferenceDto}) => {
  const virarPauta = useCreateTrendTopic();

  return (
    <article
      className="dm-piece dm-viral"
      data-testid="curated-row"
      data-credit={referencia.creditTo}
    >
      <span className="dm-piece-thumb dm-viral-thumb">
        <small>{FORMATO_LABEL[referencia.mediaType]}</small>
        <small>curada</small>
      </span>

      <div className="dm-viral-body">
        <div className="dm-viral-head">
          <span>
            <strong className="dm-piece-title">{referencia.creditTo}</strong>
            <small className="dm-piece-meta">
              {`${FORMATO_LABEL[referencia.mediaType]} · publicado em ${formatDateTime(referencia.publishedAt)}`}
            </small>
          </span>
          <span className="dm-state">curadoria humana</span>
        </div>

        <small className="dm-piece-meta">
          {`Curada por ${referencia.curatedBy} em ${formatDateTime(referencia.curatedAt)} · direito: ${DIREITOS_TEXTO[referencia.rights]}`}
        </small>

        {/*
          O motivo é texto NOSSO, escrito por quem curou. Nenhuma legenda de
          terceiro aparece aqui, e a tabela nem tem onde guardar uma.
        */}
        <p className="dm-viral-caption">{referencia.note}</p>

        <small className="dm-piece-meta">
          Sem nota e sem números: ninguém mediu este post, e uma pontuação
          calculada sobre número que não existe seria invenção. O que sustenta
          esta linha é o julgamento de uma pessoa, com o direito declarado.
        </small>

        {virarPauta.error ? (
          <div className="dm-alert" role="alert">
            {describeProblem(virarPauta.error).message}
          </div>
        ) : null}

        <div className="dm-actions">
          <a
            className="dm-action"
            href={referencia.permalink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir a referência
          </a>
          {referencia.topicId === null ? (
            <button
              className="dm-action dm-action-primary"
              type="button"
              disabled={virarPauta.isPending}
              onClick={() => {
                virarPauta.mutate(referencia.id);
              }}
            >
              Virar pauta
            </button>
          ) : (
            <span className="dm-state is-approved">já virou pauta</span>
          )}
        </div>
      </div>
    </article>
  );
};

/**
 * O selo de origem do bloco.
 *
 * Ele vive aqui, e não no título da página, porque quem sabe se a leitura é
 * medida ou ilustrativa é a resposta do radar, e o radar tem endpoint próprio:
 * o snapshot do painel não carrega bloco nenhum dele.
 */
const seloDoRadar = (radar: ViralRadarDto | undefined): string => {
  if (!radar) {
    return 'LENDO';
  }
  return radar.illustrative ? 'DADOS SIMULADOS' : 'DADOS MEDIDOS';
};

export const ViralRadarPage = () => {
  const radar = useViralRadar();
  const dado = radar.data;

  return (
    <section className="dm-panel" aria-label="Radar de virais">
      <div className="dm-panel-head">
        <h2>O que viralizou</h2>
        <span className="dm-illustrative">{seloDoRadar(dado)}</span>
      </div>

      {radar.isPending ? (
        <p className="dm-subtitle" role="status">
          Carregando o radar…
        </p>
      ) : null}

      {radar.error ? (
        <div className="dm-alert" role="alert">
          {describeProblem(radar.error).message}
        </div>
      ) : null}

      {dado?.availability === 'unavailable' ? (
        <RadarIndisponivel reason={dado.reason} graphCode={dado.graphCode} />
      ) : null}

      {/*
        O VAZIO VERDADEIRO, e agora ele diz de onde tirou essa certeza.
        A frase antiga ("os perfis foram consultados") saía deste mesmo lugar
        sem nenhuma rodada por trás. Agora ela é ancorada no recibo, e cita a
        contagem de perfis lidos: quem discorda tem o número para conferir.
      */}
      {dado?.availability === 'available' && dado.posts.length === 0 ? (
        <p className="dm-subtitle" data-testid="radar-vazio">
          {dado.lastRun === null
            ? 'Nenhuma mídia na janela, e nenhuma rodada registrada para explicar por quê.'
            : `O radar rodou em ${formatDateTime(dado.lastRun.startedAt)} e leu ${CONTAGEM.format(dado.lastRun.profilesRead)} perfil(s). Aqui houve leitura, e nenhuma mídia passou da régua: isto é o mercado, e não uma falha nossa.`}
        </p>
      ) : null}

      {dado?.availability === 'available' && dado.posts.length > 0 ? (
        <>
          <p className="dm-subtitle">
            {`Leitura do dia ${diaLocal(dado.localDate)}, ordenada pela nota.`}
          </p>

          <div className="dm-queue">
            {dado.posts.map((post) => (
              <ViralRow key={post.id} post={post} />
            ))}
          </div>

          <p className="dm-subtitle dm-viral-note">
            O &quot;Compartilhar no story&quot; é gesto seu, no aplicativo do
            Instagram, e é ele que credita o autor automaticamente. Esta tela
            lê, ranqueia e abre o original; ela não publica mídia de ninguém, e
            não finge que publica.
          </p>
        </>
      ) : null}

      {/*
        O recibo e a montagem ficam nos DOIS estados.
        "Quando foi a última leitura?" e "isto está montado?" são perguntas que
        o dono faz com a lista cheia e com a lista vazia, e esconder a resposta
        num dos casos é o que fazia a tela parecer coerente enquanto mentia.
      */}
      {dado ? (
        <>
          {dado.lastRun === null ? (
            <p className="dm-piece-meta" data-testid="radar-sem-recibo">
              Nenhuma rodada registrada até agora. Este espaço mostra a data, o
              desfecho e as contagens da última leitura assim que existir uma.
            </p>
          ) : (
            <ReciboDaRodada recibo={dado.lastRun} />
          )}
          <MontagemDoRadar setup={dado.setup} />
        </>
      ) : null}

      {/*
        A CURADORIA, e ela aparece nos dois estados de propósito.
        Enquanto a descoberta automática estiver indisponível, este é o caminho
        que produz pauta. Sumir com ele junto com o motivo da indisponibilidade
        deixaria o dono sem saída na única tela que ele abre para isso.
      */}
      {dado ? (
        <div className="dm-viral-curadoria">
          <p className="dm-subtitle">Referências curadas por gente</p>
          {dado.curated.length === 0 ? (
            <p className="dm-piece-meta" data-testid="curadoria-vazia">
              {dado.setup.curadoriaConfigurada
                ? 'Nenhuma referência curada entrou até agora. Elas vêm da lista curada e entram pelo comando de referências curadas.'
                : 'A lista de referências curadas não está configurada neste ambiente. Enquanto o radar externo não existir, ela é o caminho que produz pauta.'}
            </p>
          ) : (
            <div className="dm-queue">
              {dado.curated.map((referencia) => (
                <CuratedRow key={referencia.id} referencia={referencia} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
};
